import type { CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';

/**
 * Maps productStrategyId → display label for the given campaigns' strategies.
 * Label is `name`, falling back to strategyId → pageId → productStrategyId.
 */
export function buildStrategyLabelMap (
  campaigns: Array<Pick<CampaignDesiredStateDto, 'productStrategies'>> | undefined,
): Map<string, string> {
  const byId = new Map<string, string>();
  for (const campaign of campaigns ?? []) {
    for (const ps of campaign.productStrategies ?? []) {
      if (!ps.productStrategyId || byId.has(ps.productStrategyId)) continue;
      // find(Boolean) skips empty/missing values (unlike ??).
      const label = [ps.name?.trim(), ps.strategyId, ps.pageId, ps.productStrategyId].find(Boolean) ?? ps.productStrategyId;
      byId.set(ps.productStrategyId, label);
    }
  }
  return byId;
}

/**
 * Resolves one productStrategyId to its label — the id itself if unmapped, or '' if absent.
 */
export function resolveStrategyLabel (
  labels: Map<string, string> | undefined,
  productStrategyId: string | undefined,
): string {
  if (!productStrategyId) return '';
  return labels?.get(productStrategyId) ?? productStrategyId;
}

/**
 * Builds the product-strategy filter dropdown options from the in-scope campaigns: one option per
 * distinct productStrategyId (deduped across campaigns), labeled via `labels`. Strategies without a
 * productStrategyId are skipped.
 */
export function buildStrategyFilterOptions (
  campaigns: Array<Pick<CampaignDesiredStateDto, 'productStrategies'>>,
  labels: Map<string, string> | undefined,
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  const options: Array<{ value: string; label: string }> = [];
  for (const c of campaigns) {
    for (const ps of (c.productStrategies ?? []) as Array<{ productStrategyId?: string }>) {
      if (!ps.productStrategyId || seen.has(ps.productStrategyId)) continue;
      seen.add(ps.productStrategyId);
      options.push({ value: ps.productStrategyId, label: resolveStrategyLabel(labels, ps.productStrategyId) });
    }
  }
  return options;
}
